import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { Camera, ChevronUp, ChevronDown, Check, Trash2, Droplets } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";

// ---------------------------------------------------------------------------
// Region drawing component — draw rectangle around dip points on NVR image
// ---------------------------------------------------------------------------
const REGION_COLOR = "#3b82f6";
const MIN_RECT_SIZE = 15;

function DipRegionDrawer({ imageUrl, existingRegion, onSave, onCancel, saving }) {
    const [region, setRegion] = useState(existingRegion || null);
    const [drawing, setDrawing] = useState(false);
    const [drawStart, setDrawStart] = useState(null);
    const [drawCurrent, setDrawCurrent] = useState(null);
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
    const canvasRef = useRef(null);
    const imgRef = useRef(null);

    const handleImageLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img) return;
        const rect = img.getBoundingClientRect();
        setImgSize({ w: rect.width, h: rect.height });
        const canvas = canvasRef.current;
        if (canvas) {
            canvas.width = rect.width;
            canvas.height = rect.height;
        }
    }, []);

    // Redraw canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || imgSize.w === 0) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const drawRect = (r, dashed) => {
            if (!r) return;
            const x = r.x * canvas.width;
            const y = r.y * canvas.height;
            const w = r.w * canvas.width;
            const h = r.h * canvas.height;

            ctx.fillStyle = REGION_COLOR + "25";
            ctx.fillRect(x, y, w, h);
            ctx.strokeStyle = REGION_COLOR;
            ctx.lineWidth = 2;
            ctx.setLineDash(dashed ? [6, 4] : []);
            ctx.strokeRect(x, y, w, h);
            ctx.setLineDash([]);

            // Label
            ctx.fillStyle = REGION_COLOR;
            ctx.font = "bold 14px sans-serif";
            ctx.fillText("Dip Point", x + 6, y + 18);
        };

        // Draw saved region
        if (region) {
            drawRect(region, false);
        }

        // Draw in-progress rectangle
        if (drawing && drawStart && drawCurrent) {
            const x = Math.min(drawStart.x, drawCurrent.x) / canvas.width;
            const y = Math.min(drawStart.y, drawCurrent.y) / canvas.height;
            const w = Math.abs(drawCurrent.x - drawStart.x) / canvas.width;
            const h = Math.abs(drawCurrent.y - drawStart.y) / canvas.height;
            drawRect({ x, y, w, h }, true);
        }
    }, [region, drawing, drawStart, drawCurrent, imgSize]);

    function getCanvasCoords(e) {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        return {
            x: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
            y: Math.max(0, Math.min(e.clientY - rect.top, rect.height)),
        };
    }

    function handleMouseDown(e) {
        if (e.button !== 0) return;
        setDrawing(true);
        const coords = getCanvasCoords(e);
        setDrawStart(coords);
        setDrawCurrent(coords);
    }

    function handleMouseMove(e) {
        if (!drawing) return;
        setDrawCurrent(getCanvasCoords(e));
    }

    function handleMouseUp(e) {
        if (!drawing) return;
        setDrawing(false);
        const end = getCanvasCoords(e);
        const w = Math.abs(end.x - drawStart.x);
        const h = Math.abs(end.y - drawStart.y);

        if (w < MIN_RECT_SIZE || h < MIN_RECT_SIZE) {
            setDrawStart(null);
            setDrawCurrent(null);
            return;
        }

        const canvas = canvasRef.current;
        const newRegion = {
            x: Math.min(drawStart.x, end.x) / canvas.width,
            y: Math.min(drawStart.y, end.y) / canvas.height,
            w: w / canvas.width,
            h: h / canvas.height,
        };
        setRegion(newRegion);
        setDrawStart(null);
        setDrawCurrent(null);
    }

    return (
        <div>
            <div style={{ marginBottom: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                Draw a rectangle around the dip point area. Click and drag on the image.
            </div>
            <div style={{ position: "relative", display: "inline-block" }}>
                <img
                    ref={imgRef}
                    src={imageUrl}
                    alt="NVR Camera"
                    style={{ maxWidth: "100%", maxHeight: 500, display: "block", borderRadius: 6 }}
                    onLoad={handleImageLoad}
                    draggable={false}
                />
                <canvas
                    ref={canvasRef}
                    style={{
                        position: "absolute", top: 0, left: 0,
                        width: "100%", height: "100%",
                        cursor: "crosshair",
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => {
                        if (drawing) {
                            setDrawing(false);
                            setDrawStart(null);
                            setDrawCurrent(null);
                        }
                    }}
                />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                {region && (
                    <button
                        className="preset-action-btn"
                        onClick={() => setRegion(null)}
                        style={{ color: "var(--red, #d32f2f)" }}
                    >
                        Clear Region
                    </button>
                )}
                <div style={{ flex: 1 }} />
                <button
                    className="preset-action-btn"
                    onClick={onCancel}
                    style={{ padding: "6px 16px" }}
                >
                    Cancel
                </button>
                <button
                    className="action-submit"
                    onClick={() => onSave(region)}
                    disabled={saving}
                    style={{ padding: "6px 16px", fontSize: 13 }}
                >
                    {saving ? "Saving..." : "Save Dip Preset"}
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Status badge config
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
    configured: { label: "CONFIGURED", bg: "rgba(34,197,94,0.15)", color: "#22c55e" },
    has_nvr:    { label: "HAS NVR",    bg: "rgba(59,130,246,0.15)", color: "#3b82f6" },
    no_nvr:     { label: "NO NVR",     bg: "rgba(100,100,100,0.1)", color: "#888" },
    nvr_down:   { label: "NVR DOWN",   bg: "rgba(239,68,68,0.15)", color: "#ef4444" },
};

// ---------------------------------------------------------------------------
// Site selector — list of stores with dip preset status
// ---------------------------------------------------------------------------
function DipSiteSelector() {
    const navigate = useNavigate();
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ states: [] });
    const [stateFilter, setStateFilter] = useState("");
    const [search, setSearch] = useState("");
    const [sortCol, setSortCol] = useState("siteId");
    const [sortDir, setSortDir] = useState("asc");

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [siteData, filterData] = await Promise.all([
                api.dips.sites({ state: stateFilter }),
                api.dips.filters(),
            ]);
            setSites(siteData.sites || []);
            setFilters(filterData);
        } catch (e) {
            console.error("Failed to load dip sites", e);
        } finally {
            setLoading(false);
        }
    }, [stateFilter]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortCol(col); setSortDir("asc"); }
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return null;
        return sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
    };

    const filteredSites = useMemo(() => {
        let list = [...sites];
        if (search) {
            const term = search.toLowerCase();
            list = list.filter(s =>
                (s.siteId && String(s.siteId).toLowerCase().includes(term)) ||
                (s.name && s.name.toLowerCase().includes(term))
            );
        }
        list.sort((a, b) => {
            let cmp = 0;
            if (sortCol === "siteId") cmp = (a.siteId || "").localeCompare(b.siteId || "", undefined, { numeric: true });
            else if (sortCol === "name") cmp = (a.name || "").localeCompare(b.name || "");
            else if (sortCol === "state") cmp = (a.stateCode || "").localeCompare(b.stateCode || "");
            else if (sortCol === "status") {
                const order = { configured: 0, has_nvr: 1, nvr_down: 2, no_nvr: 3 };
                cmp = (order[a._status] ?? 9) - (order[b._status] ?? 9);
            }
            return sortDir === "asc" ? cmp : -cmp;
        });
        return list;
    }, [sites, search, sortCol, sortDir]);

    // Compute status for display
    const enriched = useMemo(() => filteredSites.map(s => {
        let _status = "no_nvr";
        if (s.hasDipPreset) _status = "configured";
        else if (s.nvrStatus === "OK") _status = "has_nvr";
        else if (s.nvrStatus !== "NO_NVR") _status = "nvr_down";
        return { ...s, _status };
    }), [filteredSites]);

    const counts = useMemo(() => {
        const c = { configured: 0, has_nvr: 0, nvr_down: 0, no_nvr: 0 };
        enriched.forEach(s => { c[s._status] = (c[s._status] || 0) + 1; });
        return c;
    }, [enriched]);

    if (loading) return <LoadingSpinner message="Loading sites..." />;

    const thStyle = {
        padding: "8px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: 0.5, color: "var(--text-secondary)", borderBottom: "1px solid var(--border)",
        cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", textAlign: "left",
    };
    const tdStyle = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))" };

    return (
        <div>
            <div className="page-header">
                <h2>Dip Configuration</h2>
                <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                    Configure NVR camera presets to monitor fuel tank dip points
                </p>
            </div>

            {/* Summary cards */}
            <div style={{ display: "flex", gap: 10, padding: "0 0 16px", flexWrap: "wrap" }}>
                {[
                    { key: "configured", label: "Configured", count: counts.configured },
                    { key: "has_nvr", label: "Has NVR", count: counts.has_nvr },
                    { key: "nvr_down", label: "NVR Down", count: counts.nvr_down },
                    { key: "no_nvr", label: "No NVR", count: counts.no_nvr },
                ].map(c => (
                    <div key={c.key} className="card" style={{ padding: "10px 16px", minWidth: 100, textAlign: "center" }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: STATUS_CONFIG[c.key].color }}>{c.count}</div>
                        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>{c.label}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16, alignItems: "center" }}>
                <select className="filter-select" value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All States</option>
                    {filters.states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                </select>
                <input
                    type="text" className="filter-select" placeholder="Search site..."
                    value={search} onChange={e => setSearch(e.target.value)}
                    style={{ fontSize: 12, minWidth: 160 }}
                />
            </div>

            {/* Sites table */}
            <div className="card" style={{ overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={thStyle} onClick={() => handleSort("siteId")}>Site <SortIcon col="siteId" /></th>
                            <th style={thStyle} onClick={() => handleSort("name")}>Name <SortIcon col="name" /></th>
                            <th style={thStyle} onClick={() => handleSort("state")}>State <SortIcon col="state" /></th>
                            <th style={thStyle}>NVR</th>
                            <th style={thStyle}>Cameras</th>
                            <th style={thStyle} onClick={() => handleSort("status")}>Dip Status <SortIcon col="status" /></th>
                            <th style={{ ...thStyle, cursor: "default" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {enriched.length === 0 && (
                            <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)", padding: 32 }}>
                                No sites match the current filters
                            </td></tr>
                        )}
                        {enriched.map(s => {
                            const cfg = STATUS_CONFIG[s._status];
                            const clickable = s._status !== "no_nvr";
                            return (
                                <tr key={s.siteId}
                                    style={{ cursor: clickable ? "pointer" : "default" }}
                                    onClick={() => { if (clickable) navigate(`/dips/${s.siteId}`); }}
                                    onMouseEnter={e => { if (clickable) e.currentTarget.style.background = "var(--bg-hover)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                                >
                                    <td style={tdStyle}><strong>{s.siteId}</strong></td>
                                    <td style={tdStyle}>{s.name || "--"}</td>
                                    <td style={tdStyle}>{s.stateCode || "--"}</td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            width: 8, height: 8, borderRadius: "50%", display: "inline-block",
                                            background: s.nvrStatus === "OK" ? "#4caf50" : s.nvrStatus === "NO_NVR" ? "#888" : "#f44336",
                                            marginRight: 6,
                                        }} />
                                        {s.nvrIp || "--"}
                                    </td>
                                    <td style={tdStyle}>{s.cameraCount || "--"}</td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                                            background: cfg.bg, color: cfg.color,
                                        }}>{cfg.label}{s.dipPresetCount > 1 ? ` (${s.dipPresetCount})` : ""}</span>
                                    </td>
                                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                                        {clickable && (
                                            <button
                                                className="preset-action-btn"
                                                onClick={() => navigate(`/dips/${s.siteId}`)}
                                                style={{ padding: "4px 10px", fontSize: 11 }}
                                            >{s.hasDipPreset ? "Manage" : "Configure"}</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Site detail — pick NVR camera channel and draw dip region
// ---------------------------------------------------------------------------
function DipSiteDetail() {
    const { siteId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nvr, setNvr] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [presets, setPresets] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [drawingMode, setDrawingMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalImage, setModalImage] = useState(null);

    // Build a lookup: channel -> preset
    const presetByChannel = useMemo(() => {
        const map = {};
        presets.forEach(p => { map[p.camera_channel] = p; });
        return map;
    }, [presets]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.dips.cameras(siteId);
            setNvr(data.nvr);
            setCameras(data.cameras || []);
            setPresets(data.presets || []);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [siteId]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSave = async (region) => {
        setSaving(true);
        try {
            const selectedCam = cameras.find(c => c.channel === selectedChannel);
            await api.dips.savePreset(siteId, {
                channel: selectedChannel,
                regionJson: region,
                referenceImageUrl: selectedCam?.blobName || null,
            });
            await loadData();
            setDrawingMode(false);
        } catch (err) {
            alert(`Save failed: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (presetId, channel) => {
        if (!window.confirm(`Remove dip preset for channel ${channel}?`)) return;
        try {
            await api.dips.deletePreset(siteId, presetId);
            await loadData();
        } catch (err) {
            alert(`Delete failed: ${err.message}`);
        }
    };

    if (loading) return <LoadingSpinner message="Loading cameras..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load cameras</h3>
                <p>{error}</p>
                <Link to="/dips" style={{ color: "var(--accent)" }}>Back to Site List</Link>
            </div>
        );
    }

    const selectedCam = cameras.find(c => c.channel === selectedChannel);
    const selectedPreset = presetByChannel[selectedChannel];
    const selectedRegion = selectedPreset?.region_json ? JSON.parse(selectedPreset.region_json) : null;

    return (
        <div>
            <div className="page-header">
                <p style={{ marginBottom: 4 }}>
                    <Link to="/dips" style={{ color: "var(--accent)", textDecoration: "none", fontSize: 13 }}>
                        &larr; Back to Site List
                    </Link>
                </p>
                <h2>Dip Configuration &mdash; Site {siteId}</h2>
                <p style={{ color: "var(--text-secondary)" }}>
                    NVR: {nvr?.ip || "Unknown"} &mdash; {nvr?.cameras_total || 0} cameras
                    &mdash; {presets.length} dip preset{presets.length !== 1 ? "s" : ""} configured
                </p>
            </div>

            {/* Existing preset summaries */}
            {presets.length > 0 && !drawingMode && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                    {presets.map(p => {
                        const region = p.region_json ? JSON.parse(p.region_json) : null;
                        return (
                            <div key={p.id} className="card" style={{ padding: "12px 16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <Droplets size={18} style={{ color: "#3b82f6" }} />
                                    <div style={{ flex: 1 }}>
                                        <span style={{ fontWeight: 600 }}>Channel {p.camera_channel}</span>
                                        <span style={{ fontSize: 12, color: "var(--text-secondary)", marginLeft: 8 }}>
                                            by {p.configured_by} on {new Date(p.configured_at).toLocaleDateString()}
                                        </span>
                                        {region && (
                                            <span style={{ fontSize: 11, color: "var(--text-secondary)", marginLeft: 8 }}>
                                                ({Math.round(region.w * 100)}% x {Math.round(region.h * 100)}%)
                                            </span>
                                        )}
                                    </div>
                                    <button className="preset-action-btn" onClick={() => {
                                        setSelectedChannel(p.camera_channel);
                                        setDrawingMode(true);
                                    }} style={{ padding: "4px 10px", fontSize: 11 }}>
                                        Reconfigure
                                    </button>
                                    <button className="preset-action-btn" onClick={() => handleDelete(p.id, p.camera_channel)}
                                        style={{ padding: "4px 10px", fontSize: 11, color: "var(--red, #d32f2f)" }}
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Drawing mode — selected camera with region drawer */}
            {drawingMode && selectedCam && (
                <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                    <h3 style={{ margin: "0 0 12px", fontSize: 14 }}>
                        Configure Dip Preset &mdash; Channel {selectedChannel}
                    </h3>
                    <DipRegionDrawer
                        imageUrl={selectedCam.imageUrl}
                        existingRegion={selectedRegion}
                        onSave={handleSave}
                        onCancel={() => setDrawingMode(false)}
                        saving={saving}
                    />
                </div>
            )}

            {/* Camera grid — pick channel */}
            <h3 style={{ margin: "20px 0 10px", fontSize: 14, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 1 }}>
                NVR Channels ({cameras.length})
            </h3>
            {cameras.length === 0 ? (
                <div className="empty-state">
                    <h3>No Camera Images</h3>
                    <p>No hourly snapshots found for this site. The NVR may be offline or not yet configured for hourly captures.</p>
                </div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                    {cameras.map(cam => {
                        const isSelected = cam.channel === selectedChannel && drawingMode;
                        const camPreset = presetByChannel[cam.channel];
                        const camRegion = camPreset?.region_json ? JSON.parse(camPreset.region_json) : null;
                        return (
                            <div key={cam.channel}
                                className="preset-card"
                                style={{
                                    border: isSelected ? "2px solid var(--accent)" : undefined,
                                    opacity: camPreset ? 1 : 0.85,
                                }}
                            >
                                <div className="preset-card-image-wrap">
                                    <img
                                        className="preset-card-image"
                                        src={cam.imageUrl}
                                        alt={`Channel ${cam.channel}`}
                                        onClick={() => setModalImage({ src: cam.imageUrl, alt: `Channel ${cam.channel}` })}
                                    />
                                    {camRegion && (
                                        <div style={{
                                            position: "absolute",
                                            top: `${camRegion.y * 100}%`,
                                            left: `${camRegion.x * 100}%`,
                                            width: `${camRegion.w * 100}%`,
                                            height: `${camRegion.h * 100}%`,
                                            border: "2px solid #3b82f6",
                                            background: "rgba(59,130,246,0.15)",
                                            pointerEvents: "none",
                                            borderRadius: 2,
                                        }} />
                                    )}
                                </div>
                                <div className="preset-card-body">
                                    <div className="preset-card-header">
                                        <span className="preset-card-name">Channel {cam.channel}</span>
                                        {camPreset && (
                                            <span style={{
                                                padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                                                background: "rgba(34,197,94,0.15)", color: "#22c55e", marginLeft: 8,
                                            }}>DIP</span>
                                        )}
                                    </div>
                                    <div className="preset-card-detail">
                                        <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                                            {cam.date}
                                        </span>
                                    </div>
                                    <div className="preset-card-actions">
                                        <button
                                            className="preset-action-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedChannel(cam.channel);
                                                setDrawingMode(true);
                                            }}
                                            style={{ flex: 1 }}
                                        >
                                            {camPreset ? (
                                                <><Check size={13} /> Reconfigure</>
                                            ) : (
                                                <><Droplets size={13} /> Set as Dip Cam</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Image lightbox */}
            {modalImage && (
                <div className="image-modal-overlay" onClick={() => setModalImage(null)}>
                    <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="image-modal-close" onClick={() => setModalImage(null)}>ESC</button>
                        <img className="image-modal-img" src={modalImage.src} alt={modalImage.alt} />
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main export — routes to either site list or detail
// ---------------------------------------------------------------------------
export default function DipMonitoring() {
    const { siteId } = useParams();
    if (!siteId) return <DipSiteSelector />;
    return <DipSiteDetail />;
}
